import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useAreaConfig } from '@/hooks';
import { useAdminSubjectDraft, SubjectDraft } from '@/contexts';
import { ADMIN_SCROLLBAR_CLASSES } from '../constants';
import { AccessDenied, NotFound, ServerError } from '../../Common';
import { WizardStepNav } from './components/WizardStepNav';
import { Step1BasicDetails } from './steps/Step1BasicDetails';
import { Step2ThreeDConfig } from './steps/Step2ThreeDConfig';
import { Step3ContextModels } from './steps/Step3ContextModels';
import { Step4Categories } from './steps/Step4Categories';
import { Step5Labels } from './steps/Step5Labels';
import { Step6Preview } from './steps/Step6Preview';
import { Step6Export } from './steps/Step6Export';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui/dialog';

const STEP_LABELS = ['Basic Details', '3D Config', 'Context Models', 'Categories', 'Labels', 'Preview', 'Export'];
const TOTAL_STEPS = 7;

/** A field is satisfied if it has a blob URL (file uploaded this session) or a non-empty storage path. */
const hasFile = (blobUrl: string | undefined, path: string) => !!blobUrl || path.trim() !== '';

/**
 * Returns a list of human-readable error messages for the current step.
 * Returns an empty array when the step is valid or validation is not required
 * (editing an existing subject).
 */
const getStepErrors = (step: number, draft: SubjectDraft, isNew: boolean): string[] => {
  if (!isNew) return [];

  switch (step) {
    case 1: {
      const errors: string[] = [];
      if (!draft.title.trim())       errors.push('Title is required');
      if (!draft.slug.trim())        errors.push('Slug is required');
      if (draft.disciplineId === null) errors.push('Discipline is required');
      return errors;
    }
    case 2: {
      if (draft.type !== '3D') return [];
      const errors: string[] = [];
      if (!hasFile(draft.config3D._modelBlobUrl, draft.config3D.modelPath))
        errors.push('Subject FBX is required');
      if (!hasFile(draft.config3D._textureBlobUrl, draft.config3D.texturePath))
        errors.push('Subject Texture is required');
      return errors;
    }
    case 3: {
      if (draft.type !== '3D') return [];
      const errors: string[] = [];
      if (!hasFile(draft.config3D._referenceContextBlobUrl, draft.config3D.referenceContextModelPath))
        errors.push('Reference Context FBX is required');
      if (draft.config3D.context.subjectContextEnabled &&
          !hasFile(draft.config3D._contextBlobUrl, draft.config3D.contextModelPath))
        errors.push('Context Model FBX is required (Subject Context is enabled)');
      return errors;
    }
    default:
      return [];
  }
};

export const AddSubjectWizard = () => {
  const { areaSlug } = useParams({ strict: false }) as { areaSlug: string };
  const { data: areaConfig, isLoading, error } = useAreaConfig(areaSlug, 'EDITOR');
  const { draft, updateDraft, resetDraft, hasChanges, isNew } = useAdminSubjectDraft();
  const stepErrors = getStepErrors(draft.currentStep, draft, isNew);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const goToStep = (step: number) => {
    updateDraft({ currentStep: step });
  };

  const next = () => {
    if (draft.currentStep >= TOTAL_STEPS) return;
    const nextStep = draft.currentStep + 1;
    updateDraft({
      currentStep: nextStep,
      highestStepReached: Math.max(draft.highestStepReached, nextStep)
    });
  };

  const prev = () => {
    if (draft.currentStep > 1) updateDraft({ currentStep: draft.currentStep - 1 });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-[#999]">
        Loading area configuration…
      </div>
    );
  }

  if (error) {
    switch (error.data?.code) {
      case 'FORBIDDEN':
        return <AccessDenied error={error.data.accessError as any} />;
      case 'NOT_FOUND':
        return <NotFound message="The area you were trying to access could not be found." />;
      default:
        return <ServerError />;
    }
  }

  if (!areaConfig) {
    return <ServerError />;
  }

  const renderStep = () => {
    switch (draft.currentStep) {
      case 1:
        return <Step1BasicDetails areaConfig={areaConfig} />;
      case 2:
        return <Step2ThreeDConfig areaConfig={areaConfig} />;
      case 3:
        return <Step3ContextModels areaConfig={areaConfig} />;
      case 4:
        return <Step4Categories areaConfig={areaConfig} />;
      case 5:
        return <Step5Labels areaConfig={areaConfig} />;
      case 6:
        return <Step6Preview areaConfig={areaConfig} />;
      case 7:
        return <Step6Export areaConfig={areaConfig} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 56px)' }}>
      <WizardStepNav
        currentStep={draft.currentStep}
        totalSteps={TOTAL_STEPS}
        stepLabels={STEP_LABELS}
        maxAccessibleStep={draft.highestStepReached}
        onStepClick={goToStep}
      />

      <div className={`flex-1 overflow-y-auto px-6 py-6 ${ADMIN_SCROLLBAR_CLASSES}`}>
        {renderStep()}
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-[#333]">
        <button
          onClick={prev}
          disabled={draft.currentStep === 1}
          className="px-5 py-2 text-sm rounded border border-[#555] text-[#CCC] hover:bg-[#333] transition-colors disabled:opacity-30 disabled:cursor-default"
        >
          ← Prev
        </button>

        <div className="flex flex-col items-center gap-1.5">
          <span className="text-xs text-[#666]">Step {draft.currentStep} of {TOTAL_STEPS}</span>
          {hasChanges && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="text-xs text-red-400 hover:text-red-300 transition-colors underline underline-offset-2"
            >
              Discard changes
            </button>
          )}
        </div>

        {draft.currentStep < TOTAL_STEPS ? (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={next}
              disabled={stepErrors.length > 0}
              className="px-5 py-2 text-sm rounded bg-[#C3D82E] text-[#1A1A1A] font-semibold hover:bg-[#D4E845] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#C3D82E]"
            >
              Next →
            </button>
            {stepErrors.map((err) => (
              <p key={err} className="text-xs text-red-400 text-right">{err}</p>
            ))}
          </div>
        ) : (
          <div className="w-24" />
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm bg-[#222] border-[#444] text-[#E8E8E8]" hideXButton>
          <DialogHeader>
            <DialogTitle className="text-[#E8E8E8]">Discard all changes?</DialogTitle>
            <DialogDescription className="text-[#999]">
              This will reset the form back to its original state. Any unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <DialogClose asChild>
              <button className="px-4 py-2 text-sm rounded border border-[#555] text-[#CCC] hover:bg-[#333] transition-colors">
                Cancel
              </button>
            </DialogClose>
            <button
              onClick={() => { resetDraft(); setConfirmOpen(false); }}
              className="px-4 py-2 text-sm rounded bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors"
            >
              Discard changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
